import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { fmtUSD } from "@/lib/format";
import { Trophy, Medal } from "lucide-react";
import { EmptyState } from "@/components/common";

const TIER_STYLE = {
  Platinum: "bg-slate-200 text-slate-800",
  Gold: "bg-amber-100 text-amber-800",
  Silver: "bg-zinc-200 text-zinc-700",
  Bronze: "bg-orange-100 text-orange-800",
  Starter: "bg-secondary text-muted-foreground",
};

export const ReferralLeaderboard = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try { const { data: d } = await api.get("/referrals/leaderboard"); setData(d); }
      catch (e) { console.error("Failed to load leaderboard:", e); }
    })();
  }, []);

  if (!data) return null;
  const { you } = data;

  return (
    <Card className="p-5 shadow-premium" data-testid="referral-leaderboard">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2 text-sm font-medium"><Trophy className="h-4 w-4 text-[color:var(--tharwy-lime-700)]" />Top inviters</div>
        <Badge className={`text-[10px] ${TIER_STYLE[you.tier] || ""}`} data-testid="referral-tier">{you.tier}</Badge>
      </div>
      <p className="text-[11px] text-muted-foreground mb-4">
        {you.rank ? `You're #${you.rank} of ${data.participants} inviters` : "Invite your first friend to join the board"}
        {you.funded ? ` · ${you.funded} funded` : ""}
      </p>

      {data.leaderboard.length === 0 ? (
        <EmptyState compact testid="leaderboard-empty" title="The top spot is wide open" hint="No one has invited a friend yet." />
      ) : (
        <div className="space-y-1.5" data-testid="leaderboard-list">
          {data.leaderboard.map((r) => (
            <div key={r.rank}
              className={`flex items-center gap-3 rounded-lg px-3 py-2 ${r.is_you ? "bg-accent/60 ring-1 ring-[color:var(--tharwy-lime-700)]/30" : "bg-secondary/50"}`}
              data-testid={`leaderboard-row-${r.rank}`}>
              <span className={`numeric text-xs font-bold w-6 shrink-0 ${r.rank <= 3 ? "text-[color:var(--tharwy-lime-700)]" : "text-muted-foreground"}`}>
                {r.rank <= 3 ? <Medal className="h-4 w-4" /> : `#${r.rank}`}
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{r.is_you ? "You" : r.name}</div>
                <div className="text-[11px] text-muted-foreground">{r.funded} funded · {r.invited} invited</div>
              </div>
              <span className="numeric text-sm font-semibold shrink-0">{fmtUSD(r.earned_usd)}</span>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 pt-3 border-t flex flex-wrap gap-1.5" data-testid="leaderboard-tiers">
        {data.tiers.map((t) => (
          <span key={t.name}
            className={`rounded-full px-2.5 py-1 text-[10px] font-medium ${you.tier === t.name ? TIER_STYLE[t.name] : "bg-secondary text-muted-foreground"}`}>
            {t.name} · {t.min_funded}+
          </span>
        ))}
      </div>
    </Card>
  );
};
